import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { CoachingSwitchboardService } from '../../../../coaching-base/switchboard';
import { SwitchboardTranscriptSummaryComm } from '../../../../coaching-base/switchboard/switchboard-transcript-summary-comm.model';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { Platform } from '../../../../platform-call';
import { StateToProps } from '../../../../store';
import { useDispatcher, UseStore } from '../../../../store.decorator';
import {
    ChatRequest,
    TranscriptImportDialogService,
} from '../../../../teams-call';
import { useSelector2 } from '../../../store-use-selector';
import { TranscriptDialogService } from '../../../transcript-dialog';
import { ChatDialogData } from '../../teamschat-dialog/model/chat-dialog-data.model';
import { TeamschatDialogComponent } from '../../teamschat-dialog/teamschat-dialog.component';

import {
    bindCheckLoadMore,
    didClickLoadMore,
    resetLoadMore,
} from './function/check-load-more.function';
import { OrderBy } from './model/order-by.model';
import { TranscriptCategories } from './model/transcript-categories.model';
import { TranscriptData } from './model/transcript-data.model';
import { deleteTranscript } from './state/delete-transcript.action';
import { filterSummary } from './state/filter-summary.action';
import { reload } from './state/reload.action';
import { sortSummary } from './state/sort-summary.action';
import { toggleCategoryTranscript } from './state/toggle-category-transcript.action';
import { toggleCategory } from './state/toggle-category.action';
import { TranslationKeyMap } from './transcript-summary.i18n';
import { transcriptSummaryReducer } from './transcript-summary.reducer';
import {
    defaultState,
    TranscriptSummaryState,
} from './transcript-summary.state';

@Component({
    selector: 'cmps-coaching-detail-pillar-two-transcript-summary',
    templateUrl: './transcript-summary.component.html',
    host: {
        class: 'cmps-coaching-detail-pillar-two-transcript-summary',
    },
})
@TakeUntilDestroy()
@UseStore<TranscriptSummaryState>(
    'coachingDetail.transcriptSummary',
    transcriptSummaryReducer,
)
export class TranscriptSummaryComponent
    implements
        OnInit,
        OnDestroy,
        StateToProps<TranscriptSummaryState>,
        SwitchboardTranscriptSummaryComm
{
    state: TranscriptSummaryState;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _OrderBy = OrderBy;

    private _keywordSubj: Subject<string>;
    private _keywordSub: Subscription;

    private _chatDialogRef: CmpsDialogRef<TeamschatDialogComponent, void>;

    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _translateService: TranslateService,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _dialog: CmpsDialog,
        private readonly _transcriptDialogService: TranscriptDialogService,
        private readonly _tiService: TranscriptImportDialogService,
    ) {}

    ngOnInit(): void {
        this._switchboardService.transcriptSummary.connect(this);

        this._keywordSubj = new Subject();
        this._keywordSub = this._keywordSubj
            .asObservable()
            .pipe(
                debounceTime(500),
                tap((keyword) => {
                    useDispatcher(
                        this,
                        filterSummary({
                            opts: { ...this.state.filterOpts, keyword },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._bindCheckSummary();

        bindCheckLoadMore(this);

        /* must be called last */
        this._onResetClick();
    }

    ngOnDestroy(): void {
        this._chatDialogRef?.close();

        this._keywordSub?.unsubscribe();
        this._keywordSubj?.complete();

        this._switchboardService.transcriptSummary.disconnect();
    }

    async sbCreateChat(opts: ChatRequest): Promise<void> {
        if (this._chatDialogRef != null && !this._chatDialogRef.closed) {
            return;
        }

        const data: ChatDialogData = {
            chatRequest: opts,
        };

        this._chatDialogRef = this._dialog.open(TeamschatDialogComponent, {
            resizable: true,
            disableClose: true,
            movable: true,
            data,
        });

        return await Promise.resolve();
    }

    sbReload(expandTranscriptIds?: number[]): void {
        this._onResetClick('soft-retain', expandTranscriptIds);
    }

    /** @private **/
    _onOpenTranscriptClick(platform: Platform, transcriptId: number): void {
        (this._switchboardService.commitment.sbSave() ?? Promise.resolve())
            // opening transcript is dependent on promise.resolve
            .then(() => {
                this._transcriptDialogService.open(platform, transcriptId);
            })
            .catch((err) => {
                console.log(err);
            });
    }

    /** @private **/
    _onDeleteTranscriptClick(transcript: TranscriptData): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._10052_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_TRANSCRIPT_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    useDispatcher(
                        this,
                        deleteTranscript({
                            platform: transcript.platform,
                            transcriptId: transcript.transcriptId,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private **/
    _onOrderByClick(orderBy: OrderBy): void {
        useDispatcher(this, sortSummary({ orderBy }));
    }

    /** @private */
    _onDateRangeFilterChange(dateRange: { from: Date; to: Date }): void {
        useDispatcher(
            this,
            reload({
                parameters: {
                    userId: this._activatedRoute.snapshot.params.userId,
                    startDate: dateRange?.from,
                    endDate: dateRange?.to,
                },
                mode: 'hard-retain',
            }),
        );
    }

    /** @private */
    _onParticipantFilterClick(participant: string): void {
        if (this.state?.filterOpts?.participant === participant) {
            participant = null;
        }
        useDispatcher(
            this,
            filterSummary({ opts: { ...this.state.filterOpts, participant } }),
        );
    }

    /** @private */
    _onBehaviourIdFilterClick(behaviourId: number): void {
        if (this.state?.filterOpts?.behaviourId === behaviourId) {
            behaviourId = null;
        }
        useDispatcher(
            this,
            filterSummary({ opts: { ...this.state.filterOpts, behaviourId } }),
        );
    }

    /** @private */
    _onSubBehaviourIdFilterClick(subBehaviourId: number): void {
        if (this.state?.filterOpts?.subBehaviourId === subBehaviourId) {
            subBehaviourId = null;
        }
        useDispatcher(
            this,
            filterSummary({
                opts: { ...this.state.filterOpts, subBehaviourId },
            }),
        );
    }

    /** @private */
    _onKeywordFilterChange(value: string): void {
        this._keywordSubj.next(value);
    }

    /** private */
    _onResetClick(
        mode?: 'soft-retain' | 'hard-retain' | null,
        expandTranscriptIds?: number[],
    ): void {
        const dState = defaultState();
        useDispatcher(
            this,
            reload({
                parameters: {
                    userId: this._activatedRoute.snapshot.params.userId,
                    startDate:
                        mode == null
                            ? dState.filterOpts?.dateRange?.from
                            : this.state?.filterOpts?.dateRange?.from,
                    endDate:
                        mode == null
                            ? dState.filterOpts?.dateRange?.to
                            : this.state?.filterOpts?.dateRange?.to,
                },
                mode,
                expandTranscriptIds,
            }),
        );
    }

    /** @private */
    _onImportTranscriptsClick(): void {
        this._tiService.open(
            parseInt(this._activatedRoute.snapshot.params.userId),
        );
    }

    /** @private */
    _onCategoryOpenChange(
        category: TranscriptCategories,
        isOpen: boolean,
    ): void {
        if (category.isOpen !== isOpen) {
            if (!isOpen) {
                resetLoadMore(
                    this.state?.filteredTranscriptCategories.indexOf(category),
                );
            }
            useDispatcher(this, toggleCategory({ category, isOpen }));
        }
    }

    /** @private */
    _onCategoryTranscriptOpenChange(
        transcript: TranscriptData,
        isOpen: boolean,
    ): void {
        if (transcript.isOpen !== isOpen) {
            useDispatcher(
                this,
                toggleCategoryTranscript({
                    transcriptId: transcript.transcriptId,
                    isOpen,
                }),
            );
        }
    }

    /** @private */
    _onLoadMoreClick(category: TranscriptCategories): void {
        didClickLoadMore(
            this.state?.filteredTranscriptCategories.indexOf(category),
        );
    }

    private _checkSummary(prevState: { deleteTranscript?: boolean }): void {
        if (
            prevState?.deleteTranscript !==
                this.state.pending?.deleteTranscript &&
            this.state.pending?.deleteTranscript !== true
        ) {
            this._onResetClick('soft-retain');
        }
    }

    private _bindCheckSummary(): void {
        useSelector2<{
            deleteTranscript?: boolean;
        }>(
            (state) =>
                (
                    state?.coachingDetail
                        ?.transcriptSummary as TranscriptSummaryState
                )?.pending,
        )
            .pipe(
                tap(([prevState, _]) => {
                    this._checkSummary(prevState);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
